'use client';

import type { FirebaseApp } from 'firebase/app';
import type { IAnalyticsService } from '../types/analytics.types';
import { firebaseAnalyticsService } from './firebase-service';
import { clarityService } from './clarity-service';
import { logger } from '@/core/logger';

class DefaultAnalyticsService implements IAnalyticsService {
	private analyticsServices: IAnalyticsService[] = [
		firebaseAnalyticsService,
		clarityService,
	];

	async initialize(app: FirebaseApp): Promise<boolean> {
		let hasSuccessfulInit = false;

		for (const service of this.analyticsServices) {
			try {
				const success = await service.initialize(app);
				if (success) {
					hasSuccessfulInit = true;
				}
			} catch (error) {
				logger.error('Analytics service initialization failed', error);
			}
		}

		return hasSuccessfulInit;
	}

	logEvent(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		for (const service of this.analyticsServices) {
			try {
				service.logEvent(eventName, eventParameters);
			} catch (error) {
				logger.error('Failed to log event', error);
			}
		}
	}

	logError(
		eventName: string,
		eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		for (const service of this.analyticsServices) {
			try {
				service.logError(eventName, eventParameters);
			} catch (error) {
				logger.error('Failed to log error', error);
			}
		}
	}

	identify(
		userId: string,
		userProperties?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
	): void {
		for (const service of this.analyticsServices) {
			try {
				service.identify(userId, userProperties);
			} catch (error) {
				logger.error('Failed to identify user', error);
			}
		}
	}
}

export const analyticsService = new DefaultAnalyticsService();
